import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase/config';

const FALLBACK_BG = 'linear-gradient(135deg, #667eea 0%, #764ba2 50%, #f093fb 100%)';

const getGenderDisplay = (gender) => {
  switch (gender) {
    case 'man': return { emoji: '👨', label: 'Man' };
    case 'woman': return { emoji: '👩', label: 'Woman' };
    case 'different': return { emoji: '🌈', label: 'Different' };
    default: return null;
  }
};

const calcAge = (dob) => {
  if (!dob) return '';
  const birth = new Date(dob);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return `${age} years old`;
};

const UserProfilePanel = () => {
  const [panel, setPanel] = useState(null);
  // panel = { userId, fromPanel, userData, isOnline, isMuted, isOrganizer, currentEventId, currentUserId, alreadyReported }
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleOpen = (e) => {
      setPanel(e.detail);
      setVisible(false);
      // Trigger slide-in after mount
      requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
    };
    const handleClose = () => {
      setVisible(false);
      setTimeout(() => setPanel(null), 300);
    };

    window.addEventListener('openUserProfilePanel', handleOpen);
    window.addEventListener('closeUserProfilePanel', handleClose);
    // Keep DOM-based close working too
    window.closeUserProfilePanel = handleClose;

    return () => {
      window.removeEventListener('openUserProfilePanel', handleOpen);
      window.removeEventListener('closeUserProfilePanel', handleClose);
    };
  }, []);

  if (!panel) return null;

  const {
    userId, fromPanel, userData, isOnline,
    isMuted: initialMuted, isOrganizer, currentEventId, currentUserId, alreadyReported
  } = panel;

  const isCurrentUser = userId === currentUserId;
  const ageText = calcAge(userData.date_of_birth);
  const genderInfo = getGenderDisplay(userData.gender);
  const fallbackAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(userData.name || 'User')}&background=e8eaed&color=5f6368&size=200`;

  let backgroundStyle = FALLBACK_BG;
  if (userData.background?.trim()) {
    if (userData.background.startsWith('linear-gradient') || userData.background.startsWith('radial-gradient')) {
      backgroundStyle = userData.background;
    } else if (userData.background.startsWith('http')) {
      backgroundStyle = `url('${userData.background}') center/cover`;
    }
  }

  const statusColor = isOnline ? '#34a853' : '#9e9e9e';
  const borderColor = isOnline ? '#34a853' : '#9e9e9e';

  const handleClose = () => {
    setVisible(false);
    setTimeout(() => setPanel(null), 300);
  };

  return (
    <>
      {/* Overlay */}
      <div
        onClick={handleClose}
        style={{
          position: 'fixed', inset: 0,
          background: 'transparent',
          zIndex: 10003
        }}
      />

      {/* Panel */}
      <div
        data-slide-panel="right"
        style={{
          position: 'fixed',
          top: '79px',
          right: visible ? '0' : '-400px',
          width: '380px',
          maxWidth: 'calc(100vw - 4px)',
          height: 'calc(100vh - 79px - var(--footer-height, 50px))',
          background: 'white',
          borderBottomLeftRadius: "20px",
          borderTopLeftRadius: "20px",
          boxShadow: '-4px 0 20px rgba(0,0,0,0.08)',
          zIndex: 10004,
          transition: 'right 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          overflowY: 'auto',
          fontFamily: "'Outfit', sans-serif"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Back/Close button */}
        <button
          onClick={handleClose}
          onMouseOver={(e) => e.currentTarget.style.background = 'rgba(240,240,240,0.95)'}
          onMouseOut={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.9)'}
          style={{
            position: 'absolute', top: '20px', left: '20px',
            width: '40px', height: '40px', borderRadius: '50%',
            background: 'rgba(255,255,255,0.9)', border: 'none',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer', zIndex: 2, boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            transition: 'background 0.2s'
          }}
        >
          {fromPanel === 'request' ? (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#202124" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#202124" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          )}
        </button>

        {/* Background */}
        <div style={{ width: '100%', height: '220px', background: backgroundStyle, position: 'relative' }} />

        {/* Avatar */}
        <div style={{ position: 'absolute', top: '140px', left: '50%', transform: 'translateX(-50%)', width: '120px', height: '120px' }}>
          <div
            onClick={() => window.showFullScreenImage?.(userData.profile_picture || fallbackAvatar, userData.name)}
            onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
            onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
            style={{
              width: '120px', height: '120px', borderRadius: '50%',
              border: `4px solid ${borderColor}`, overflow: 'hidden',
              background: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              cursor: 'pointer', transition: 'transform 0.2s ease', position: 'relative'
            }}
          >
            <img
              src={userData.profile_picture || fallbackAvatar}
              alt={userData.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              onError={(e) => { e.target.src = fallbackAvatar; }}
            />
          </div>
          {/* Online dot */}
          <div style={{
            position: 'absolute', bottom: '5px', right: '5px',
            width: '24px', height: '24px', background: statusColor,
            border: '4px solid white', borderRadius: '50%', boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
          }} />
        </div>

        {/* Content */}
        <div style={{ padding: '50px 20px 20px', display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh - 220px)' }}>
          <div style={{ flex: 1 }}>
            <h2 style={{ textAlign: 'center', fontSize: '24px', fontWeight: 600, color: '#202124', margin: '0 0 24px' }}>
              {userData.name || 'User'}
            </h2>

            {userData.description && (
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '12px', fontWeight: 600, color: '#5f6368', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>DESCRIPTION</h3>
                <p style={{ fontSize: '14px', color: '#202124', lineHeight: 1.6, margin: 0, whiteSpace: 'pre-wrap' }}>{userData.description}</p>
              </div>
            )}

            {ageText && (
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '12px', fontWeight: 600, color: '#5f6368', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>AGE</h3>
                <p style={{ fontSize: '14px', color: '#202124', margin: 0 }}>{ageText}</p>
              </div>
            )}

            {genderInfo && (
              <div style={{ marginBottom: '20px' }}>
                <h3 style={{ fontSize: '12px', fontWeight: 600, color: '#5f6368', margin: '0 0 8px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>GENDER</h3>
                <p style={{ fontSize: '14px', color: '#202124', margin: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <span>{genderInfo.emoji}</span> {genderInfo.label}
                </p>
              </div>
            )}
          </div>

          {/* Actions — only for other users */}
          {!isCurrentUser && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '20px 0 10px', borderTop: '1px solid #e0e0e0', marginTop: 'auto' }}>
              {/* Mute/Unmute — only if current user is organizer */}
              {isOrganizer && currentEventId && (
                <MuteButton userId={userId} eventId={currentEventId} initialMuted={initialMuted} />
              )}

              {/* Report */}
              <button
                onClick={() => !alreadyReported && window.showReportModal?.('user', userId, userData.name, userData.email)}
                onMouseOver={(e) => { if (!alreadyReported) e.currentTarget.style.background = '#fef7f7'; }}
                onMouseOut={(e) => { e.currentTarget.style.background = 'white'; }}
                disabled={alreadyReported}
                style={{
                  background: 'white',
                  border: `1px solid ${alreadyReported ? '#e0e0e0' : '#ea4335'}`,
                  cursor: alreadyReported ? 'default' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                  color: alreadyReported ? '#9ca3af' : '#ea4335',
                  fontSize: '14px', fontWeight: 500,
                  padding: '12px 16px', borderRadius: '8px',
                  opacity: alreadyReported ? 0.7 : 1,
                  transition: 'background 0.2s'
                }}
              >
                {alreadyReported ? (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>
                    Already reported
                  </>
                ) : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    Report User
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// ─── Mute button sub-component ───────────────────────────────────────────────

const MuteButton = ({ userId, eventId, initialMuted }) => {
  const [muted, setMuted] = useState(initialMuted);
  const [loading, setLoading] = useState(false);

  const toggle = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const supabaseClient = window.supabase || supabase;
      const { data: eventData } = await supabaseClient.from('events').select('muted_users').eq('event_id', eventId).single();
      let mutedUsers = eventData?.muted_users || [];
      const newMuted = !muted;
      if (newMuted) {
        if (!mutedUsers.includes(userId)) mutedUsers.push(userId);
      } else {
        mutedUsers = mutedUsers.filter(id => id !== userId);
      }
      const { error } = await supabaseClient.from('events').update({ muted_users: mutedUsers }).eq('event_id', eventId);
      if (error) throw error;
      setMuted(newMuted);
      window.showToast?.(newMuted ? 'User has been muted' : 'User has been unmuted', 'success');
      // Sync window function for legacy callers
      if (window.toggleMuteUser) window.toggleMuteUser = async (uid, eid, cur) => toggle();
    } catch {
      window.showToast?.('Failed to update mute status', 'error');
    } finally {
      setLoading(false);
    }
  };

  const color = muted ? '#34a853' : '#f4b400';
  const hoverBg = muted ? '#f0f9f4' : '#fef9f0';

  return (
    <button
      onClick={toggle}
      disabled={loading}
      onMouseOver={(e) => { if (!loading) e.currentTarget.style.background = hoverBg; }}
      onMouseOut={(e) => { e.currentTarget.style.background = 'white'; }}
      style={{
        background: 'white', color, border: `1px solid ${color}`,
        cursor: loading ? 'not-allowed' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
        fontSize: '14px', fontWeight: 500,
        padding: '12px 16px', borderRadius: '8px',
        transition: 'background 0.2s', width: '100%',
        opacity: loading ? 0.7 : 1
      }}
    >
      {muted ? (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M11 5L6 9H2v6h4l5 4V5z" />
          <path d="M15.54 8.46a5 5 0 0 1 0 7.07" /><path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
        </svg>
      ) : (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M11 5L6 9H2v6h4l5 4V5z" />
          <line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" />
        </svg>
      )}
      {loading ? 'Updating...' : muted ? 'Unmute User' : 'Mute User'}
    </button>
  );
};

export default UserProfilePanel;